import { produce } from 'immer';

import LoadingStatus from '@libs/loading-status';
import NormalizedObjects from '@libs/redux/NormalizedObjects';

import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import {
  DOCUMENTS_EXPORT__CANCEL,
  DOCUMENTS_EXPORT__INITIALIZE,
  DOCUMENTS_EXPORT__LABEL_SELECTED,
  DOCUMENTS_EXPORT__LIST_FETCH_END,
  DOCUMENTS_EXPORT__LIST_FETCH_FAILURE,
  DOCUMENTS_EXPORT__LIST_FETCH_REQUEST,
  DOCUMENTS_EXPORT__LIST_FETCH_RESTART,
  DOCUMENTS_EXPORT__LIST_FETCH_START,
  DOCUMENTS_EXPORT__LIST_FETCH_SUCCESS,
  DOCUMENTS_EXPORT__TOTAL_DOCUMENTS_COUNT_FETCH_FAILURE,
  DOCUMENTS_EXPORT__TOTAL_DOCUMENTS_COUNT_FETCH_REQUEST,
  DOCUMENTS_EXPORT__TOTAL_DOCUMENTS_COUNT_FETCH_SUCCESS,
} from '@app/store/actionTypes';
import { DocumentsExportAction } from '@app/store/documentsExport/actions';
import DocumentListFetchRange from '@app/store/documentsExport/DocumentListFetchRange';
import DocumentListFetchRangeType from '@app/store/documentsExport/DocumentListFetchRangeType';
import { DocumentListItemType } from '@app/store/transport-layer/document/documents';

export interface DocumentsExportState extends NormalizedObjects<DocumentListItemType> {
  assigned?: boolean;
  limit: number;
  errorMessage: string | null;
  labelId: string | null;
  listFetchRange: DocumentListFetchRange | null;
  loadingStatus: LoadingStatus;
  maxPage: number;
  offset: number;
  totalDocumentsCount: number;
  documentListFetchRangeType: DocumentListFetchRangeType | null;
  totalDocumentsCountLoadingStatus: LoadingStatus;
}

const initialState: DocumentsExportState = {
  allIds: [],
  assigned: undefined,
  byId: {},
  limit: 0,
  errorMessage: null,
  labelId: null,
  listFetchRange: null,
  loadingStatus: LoadingStatus.IDLE,
  maxPage: 0,
  offset: 0,
  totalDocumentsCount: 0,
  documentListFetchRangeType: null,
  totalDocumentsCountLoadingStatus: LoadingStatus.IDLE,
};

export default function reducer(
  state: DocumentsExportState = initialState,
  action: DocumentsExportAction
) {
  switch (action.type) {
    case DOCUMENTS_EXPORT__CANCEL:
    case DOCUMENTS_EXPORT__INITIALIZE:
      return initialState;
    case DOCUMENTS_EXPORT__LABEL_SELECTED:
      return produce(state, (draft) => {
        const { assigned, labelId } = action.payload;
        draft.assigned = assigned;
        draft.labelId = labelId;
      });
    case DOCUMENTS_EXPORT__LIST_FETCH_REQUEST:
      return produce(state, (draft) => {
        const { offset } = action.payload;
        draft.offset = offset;
      });
    case DOCUMENTS_EXPORT__LIST_FETCH_SUCCESS:
      return produce(state, (draft) => {
        const { documentsList, maxPage, totalDocumentsCount } = action.payload;
        const { limit, listFetchRange, documentListFetchRangeType, offset } = draft;

        draft.maxPage = maxPage;
        draft.totalDocumentsCount = totalDocumentsCount;

        if (documentListFetchRangeType && listFetchRange) {
          const { end, endPage, start, startPage } = listFetchRange;
          const page = Math.ceil((offset + 1) / limit);
          const endIndex = endPage === 1 ? end - 1 : end - limit * (endPage - 1) - 1;
          const startIndex = startPage === 1 ? start - 1 : start - limit * (startPage - 1) - 1;

          // 뷰에서 선택한 문서 리스트 조회 옵션에 따라 문서 목록을 선별합니다.
          let saveDocumentList: DocumentListItemType[] = [];
          switch (documentListFetchRangeType) {
            case 'ALL':
              saveDocumentList = documentsList;
              // 전체 조회인 경우 최대 페이지를 마지막 페이지로 저장합니다.
              listFetchRange.endPage = maxPage;
              break;
            case 'RECENT':
              if (page !== endPage) {
                saveDocumentList = documentsList;
              } else {
                saveDocumentList = documentsList.filter((_, index) => index <= endIndex);
              }
              break;
            case 'CUSTOM':
              if (page === startPage && page === endPage) {
                saveDocumentList = documentsList.filter(
                  (_, index) => index >= startIndex && index <= endIndex
                );
              } else if (page === startPage) {
                saveDocumentList = documentsList.filter((_, index) => index >= startIndex);
              } else if (page === endPage) {
                saveDocumentList = documentsList.filter((_, index) => index <= endIndex);
              } else if (page > startPage && page < endPage) {
                saveDocumentList = documentsList;
              }
              break;
            default:
              break;
          }

          // 위에서 조건에 따라 선별한 리스트를 저장합니다.
          saveDocumentList.forEach((document) => {
            if (!(document.documentId in draft.byId)) {
              draft.allIds.push(document.documentId);
            }
            draft.byId[document.documentId] = document;
          });
        }
      });
    case DOCUMENTS_EXPORT__LIST_FETCH_FAILURE:
      return produce(state, (draft) => {
        const { message } = action.payload;
        draft.errorMessage = GLOBAL_ERROR_MESSAGE[message] || message;
        draft.loadingStatus = LoadingStatus.FAILED;
      });
    case DOCUMENTS_EXPORT__LIST_FETCH_END:
      return produce(state, (draft) => {
        draft.loadingStatus = LoadingStatus.SUCCEEDED;
      });
    case DOCUMENTS_EXPORT__LIST_FETCH_RESTART:
      return produce(state, (draft) => {
        draft.allIds = [];
        draft.byId = {};
        draft.errorMessage = null;
        draft.loadingStatus = LoadingStatus.PENDING;
        draft.maxPage = 0;
        draft.offset = draft.listFetchRange?.start
          ? Math.floor(draft.listFetchRange.start / 100) * 100
          : 0;
        draft.totalDocumentsCount = 0;
      });
    case DOCUMENTS_EXPORT__LIST_FETCH_START:
      return produce(state, (draft) => {
        const { limit, documentListFetchRangeType, range } = action.payload;
        draft.allIds = [];
        draft.byId = {};
        draft.errorMessage = null;
        draft.maxPage = 0;
        draft.totalDocumentsCount = 0;
        draft.limit = limit;
        draft.documentListFetchRangeType = documentListFetchRangeType;
        if (!range) {
          // range가 null이면 문서 전체 다운로드
          draft.listFetchRange = {
            end: 0,
            endPage: 0,
            start: 0,
            startPage: 1,
          };
          draft.offset = 0;
        } else {
          const startPage = Math.ceil((range.start || 1) / limit);
          draft.listFetchRange = {
            end: range.end,
            endPage: Math.ceil((range.end || 1) / limit),
            start: range.start,
            startPage,
          };
          const page = Math.floor((range.start - 1) / limit);
          draft.offset = range.start === 0 ? 0 : page * limit;
        }

        draft.loadingStatus = LoadingStatus.PENDING;
      });
    case DOCUMENTS_EXPORT__TOTAL_DOCUMENTS_COUNT_FETCH_REQUEST:
      return produce(state, (draft) => {
        draft.totalDocumentsCountLoadingStatus = LoadingStatus.PENDING;
      });
    case DOCUMENTS_EXPORT__TOTAL_DOCUMENTS_COUNT_FETCH_SUCCESS:
      return produce(state, (draft) => {
        draft.totalDocumentsCount = action.payload;
        draft.totalDocumentsCountLoadingStatus = LoadingStatus.SUCCEEDED;
      });
    case DOCUMENTS_EXPORT__TOTAL_DOCUMENTS_COUNT_FETCH_FAILURE:
      return produce(state, (draft) => {
        draft.totalDocumentsCountLoadingStatus = LoadingStatus.FAILED;
      });
    default:
      return state;
  }
}
